import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Radar, Activity, Radio, ListChecks, ShieldCheck, ArrowRight, Zap, Database, Lock, Smartphone, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LiveTicker } from "@/components/LiveTicker";
import { TopInjuriesStrip } from "@/components/TopInjuriesStrip";

const HERO_IMG = "https://images.unsplash.com/photo-1557174949-3b1f5b2e8fac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const DATA_PROMISE = [
  { icon: Lock, title: "Security & privacy first", desc: "Email-and-password accounts with encrypted, HTTP-only sessions. We ask for the minimum — no card needed to explore, and we never sell your data." },
  { icon: Smartphone, title: "Guests save on-device", desc: "No sign-up required. Build a watchlist as a guest and it stays private on your own device — nothing leaves until you choose to." },
  { icon: ArrowLeftRight, title: "Transfers when you're ready", desc: "Create a free account and your device watchlists move over automatically — no re-adding players, no lock-in." },
];

const PIPELINE = [
  { icon: Database, title: "Ingestion Agents", desc: "Five source agents poll rosters, injury reports, trade wires, beat writers & odds on independent schedules." },
  { icon: Radar, title: "Normalization", desc: "Resolves player identity across APIs, deduplicates, standardizes timestamps to UTC." },
  { icon: Zap, title: "Classification", desc: "Tags every event by type & certainty — questionable vs officially ruled out vs unconfirmed report." },
  { icon: Radio, title: "Relevance / Alert", desc: "Matches events to your watchlist & parlay slips — pushing only what you track." },
];

const FEATURES = [
  { icon: Activity, title: "Live Status Feed", desc: "NFL, NBA, NHL & MLB events streaming in real time, filterable by league, team & player." },
  { icon: ListChecks, title: "Parlay Risk Scanner", desc: "Drop players into a slip and instantly see a Slip Health Index flagging open status concerns." },
  { icon: ShieldCheck, title: "Source-Cited Facts", desc: "Every fact shows its source & last-updated time. Official vs reported, clearly separated." },
];

export default function Landing() {
  useEffect(() => {
    document.title = "SidelineIQ — Player Status Intel for Smarter Parlays (NFL, NBA, NHL, MLB)";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "SidelineIQ tracks every injury, scratch, trade and late lineup change across the NFL, NBA, NHL and MLB — cited, timestamped, and in your hands before the line moves. Explore free as a guest, no sign-up.");
  }, []);
  return (
    <div className="min-h-screen bg-[#080B11] text-slate-200">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#080B11]/85 border-b border-[#232D42] h-16 px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#00E5A3] flex items-center justify-center"><Radar size={18} className="text-[#04140F]" /></div>
          <span className="font-heading font-extrabold text-lg text-slate-100">Sideline<span className="text-[#00E5A3]">IQ</span></span>
        </div>
        <div className="flex items-center gap-3">
          <nav className="hidden md:flex items-center gap-5 mr-2">
            <Link to="/feed" data-testid="landing-nav-feed" className="text-sm text-slate-300 hover:text-[#00E5A3]">Feed</Link>
            <Link to="/games" data-testid="landing-nav-games" className="text-sm text-slate-300 hover:text-[#00E5A3]">Games</Link>
            <Link to="/goalies" data-testid="landing-nav-goalies" className="text-sm text-slate-300 hover:text-[#00E5A3]">Goalies</Link>
            <Link to="/live" data-testid="landing-nav-live" className="text-sm text-slate-300 hover:text-[#00E5A3]">Live Lines</Link>
          </nav>
          <Link to="/login" data-testid="landing-signin-link" className="text-sm text-slate-300 hover:text-slate-100">Sign In</Link>
          <Button asChild className="bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold rounded-lg h-9">
            <Link to="/feed" data-testid="landing-launch-btn">Launch App</Link>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="stadium" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080B11]/70 via-[#080B11]/85 to-[#080B11]" />
        </div>
        <div className="relative"><LiveTicker /></div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-3xl sq-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#00E5A3]/30 bg-[#00E5A3]/10 px-3 py-1 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00E5A3] sq-pulse" />
              <span className="font-mono text-xs uppercase tracking-widest text-[#00E5A3]">Your Edge, Before The Line Moves</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50 leading-[1.05]">
              Know before the book does.<br /><span className="text-[#00E5A3]">Your all-in-one betting edge.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 mt-6 max-w-2xl leading-relaxed">
              SidelineIQ tracks every injury, scratch, trade and late lineup change across the NFL, NBA, NHL and MLB —
              cited, timestamped, and in your hands first. The research edge sharp bettors wish they had, all in one place.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button asChild className="bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold rounded-lg h-12 px-6 text-base">
                <Link to="/feed" data-testid="hero-feed-btn">Explore the demo — no sign-up <ArrowRight size={18} className="ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-[#232D42] bg-transparent hover:bg-[#151B2B] text-slate-200 rounded-lg h-12 px-6 text-base">
                <Link to="/parlay" data-testid="hero-parlay-btn">Try Parlay Scanner</Link>
              </Button>
            </div>
            <p className="text-xs text-slate-500 font-mono mt-3">Browse the live feed, player pages & build a watchlist — free, no account needed.</p>
            <div className="flex flex-wrap gap-6 mt-10">
              {["NFL", "NBA", "NHL", "MLB"].map((l) => (
                <div key={l} className="font-mono text-sm">
                  <span className="text-[#00E5A3] font-bold">{l}</span>
                  <span className="text-slate-500 ml-2">tracked</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TopInjuriesStrip />

      {/* Pipeline */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16">
        <p className="font-mono text-xs uppercase tracking-widest text-[#00E5A3] mb-2">The Pipeline</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100 mb-10">A five-agent pipeline, working the sidelines</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PIPELINE.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="relative bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
                <span className="absolute top-4 right-4 font-mono text-xs text-slate-600">0{i + 1}</span>
                <div className="h-10 w-10 rounded-lg bg-[#00E5A3]/10 flex items-center justify-center mb-4"><Icon size={20} className="text-[#00E5A3]" /></div>
                <h3 className="font-heading font-semibold text-slate-100">{p.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-[#0F1420] border border-[#232D42] rounded-xl p-6 hover:border-[#00E5A3]/40 transition-colors">
                <div className="h-11 w-11 rounded-lg bg-[#151B2B] border border-[#232D42] flex items-center justify-center mb-4"><Icon size={22} className="text-[#38BDF8]" /></div>
                <h3 className="font-heading text-lg font-semibold text-slate-100">{f.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Data & security promise */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-2xl border border-[#232D42] bg-[#0F1420] p-8 sm:p-10">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} className="text-[#00E5A3]" />
            <p className="font-mono text-xs uppercase tracking-widest text-[#00E5A3]">Your data, your rules</p>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100 mb-3">Private by default. Yours to keep.</h2>
          <p className="text-slate-400 max-w-2xl leading-relaxed mb-8">
            Try everything without handing over your details. Save watchlists as a guest — right on your device — and if you ever want alerts and sync, an account is one step away and brings your lists with it.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {DATA_PROMISE.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.title} data-testid={`data-promise-${d.title.split(" ")[0].toLowerCase()}`} className="bg-[#151B2B] border border-[#232D42] rounded-xl p-5">
                  <div className="h-10 w-10 rounded-lg bg-[#00E5A3]/10 flex items-center justify-center mb-4"><Icon size={20} className="text-[#00E5A3]" /></div>
                  <h3 className="font-heading font-semibold text-slate-100">{d.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-2xl border border-[#232D42] bg-gradient-to-br from-[#0F1420] to-[#151B2B] p-8 sm:p-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-100">Get the jump on every game</h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">Start free in seconds. Founding members lock in lifetime-low launch pricing — while spots last.</p>
          <Button asChild className="mt-6 bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold rounded-lg h-12 px-8">
            <Link to="/register" data-testid="cta-register-btn">Create Free Account</Link>
          </Button>
        </div>
        <p className="text-center text-xs text-slate-600 font-mono mt-8 max-w-2xl mx-auto">
          SidelineIQ provides availability status and source tracking only. No bet recommendations or financial advice provided.
        </p>
      </section>
    </div>
  );
}
